export interface IBaseFilter {
  name: string;
  defaultLabel: string;
  isActive: boolean;
}

export interface IRange {
  from: number;
  to: number;
}

export class FilterRange implements IRange {
  from: number;
  to: number;

  constructor(from: number, to: number) {
    if (from === null || from === undefined) {
      throw Error('Min value must be provided.');
      return;
    }
    if (to === null || to === undefined) {
      throw Error('Max value must be provided.');
      return;
    }
    if (from > to) {
      throw Error('Min value must be larger than max value.');
      return;
    }
    this.from = from;
    this.to = to;
  }
}

export interface IFilterOption {
  count: number;
  label: string;
  name: string;
  isActive: boolean;
  isDefault: boolean;
}

export interface IFilter extends IBaseFilter {
  options: IFilterOption[];
}

export interface IRangeFilter extends IBaseFilter {
  value: IRange;
  range: IRange;
}

export abstract class FilterBase implements IBaseFilter {
  isActive: boolean;
  defaultLabel: string;
  name: string;
  hasSearch: boolean;

  abstract reset(): any;

  abstract get label(): string;

  get isRangeFilter(): boolean {
    return this instanceof RangeFilter;
  }

  constructor(name: string, defaultLabel: string, isActive: boolean, hasSearch: boolean) {
    this.name = name;
    this.defaultLabel = defaultLabel;
    this.isActive = isActive;
    this.hasSearch = hasSearch;
  }
}

export class Filter extends FilterBase implements IFilter {
  private numVisibleActiveOptions = 3;
  options: FilterOption[];

  constructor(
    name: string,
    defaultLabel: string,
    isActive = false,
    options: any[] = [],
    hasSearch = true,
  ) {
    super(name, defaultLabel, isActive, hasSearch);
    this.options = options;
  }

  reset() {
    const defaultOptionIndex = this.options.findIndex(
      (option: FilterOption) => option.isDefault,
    );
    // TODO: replace empty string with ALL_FILTER_OPTION_NAME constant
    const allOptionIsDefault =
      defaultOptionIndex > -1 && this.options[defaultOptionIndex].name === '';
    //  make all options active if all option is default
    // eslint-disable-next-line @typescript-eslint/prefer-for-of, functional/no-let
    for (let i = 0; i < this.options.length; i++) {
      const option = this.options[i];
      option.isActive = allOptionIsDefault ? true : option.isDefault;
    }
  }

  get label(): string {
    const activeOptionLabels = this.options
      // TODO: replace empty string with ALL_FILTER_OPTION_NAME constant
      .filter((option: FilterOption) => option.isActive && option.name !== '')
      .map((option: FilterOption) => option.label);
    const numActiveOptions = activeOptionLabels.length;
    if (numActiveOptions > 0) {
      if (numActiveOptions <= this.numVisibleActiveOptions) {
        return activeOptionLabels.join(', ');
      }
      const numExtraActiveOptions = numActiveOptions - this.numVisibleActiveOptions;
      return `${activeOptionLabels
        .slice(0, this.numVisibleActiveOptions)
        .join(', ')}... +${numExtraActiveOptions}`;
    }
    return this.defaultLabel;
  }

  get allOption(): FilterOption {
    // replace empty string with ALL_FILTER_OPTION_NAME constant
    // @ts-expect-error
    return this.options.find((option: FilterOption) => option.name === '');
  }
}

export class FilterOption implements IFilterOption {
  isActive: boolean;
  count: number;
  isDefault: boolean;
  label: string;
  name: string;

  constructor(
    name: string,
    label: string,
    isActive = false,
    isDefault = false,
    count = 0,
  ) {
    this.name = name;
    this.label = label;
    this.isActive = isActive;
    this.isDefault = isDefault;
    this.count = count;
  }
}

export class RangeFilter extends FilterBase implements IRangeFilter {
  range: FilterRange;
  value: FilterRange;

  constructor(
    name: string,
    defaultLabel: string,
    isActive = false,
    range: FilterRange,
    // @ts-expect-error
    value: FilterRange = null,
    hasSearch = false,
  ) {
    super(name, defaultLabel, isActive, hasSearch);
    this.range = range;
    if (value) {
      this.value = value;
    } else {
      this.value = range;
    }
  }

  reset() {
    this.value = this.range;
  }

  get label(): string {
    if (this.isActive) {
      if (this.value.from === this.range.from && this.value.to === this.range.to) {
        return `${this.defaultLabel}: All`;
      }
      return `${this.defaultLabel}: ${this.value.from} - ${this.value.to}`;
    }
    return this.defaultLabel;
  }
}

export interface SearchFilterOptionsData {
  filterOptionsSearchQuery: string;
  filter: Filter;
}

export interface RangeFilterChangeData {
  from: number;
  to: number;
  filter: RangeFilter;
}

export interface ToggleFilterOptionData {
  filterName: string;
  filterOptionName: string;
}
